import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import type { StaffPasskey } from '@garage/shared'
import { browserSupportsWebAuthn, startRegistration } from '@simplewebauthn/browser'
import { Button, TextField, useSnackbar, ConfirmDialog, Spinner } from '@/ui'
import { api, ApiError } from '@/lib/api.js'
import { qk } from '@/lib/query-keys.js'
import { useFormatters } from '@/lib/hooks.js'
import { isUserCancelled } from '@/lib/passkeys.js'

/**
 * The signed-in person's own passkeys: list, add this device, remove. Every
 * account manages only its own keys, so this is shown to everyone, not just
 * administrators (contrast StaffAccountsSection).
 */
export function PasskeysSection() {
  const { t } = useTranslation()
  const snackbar = useSnackbar()
  const queryClient = useQueryClient()
  const formatters = useFormatters()

  const supported = browserSupportsWebAuthn()
  const defaultDeviceName = t('settings.passkeys.defaultDeviceName')

  const [deviceName, setDeviceName] = useState(defaultDeviceName)
  const [error, setError] = useState<string | null>(null)
  const [removeConfirm, setRemoveConfirm] = useState<StaffPasskey | null>(null)

  const { data, isLoading } = useQuery({
    queryKey: qk.passkeys,
    queryFn: () => api.auth.listPasskeys(),
  })

  // Server validation (e.g. "needs HTTPS") and an already-registered device are
  // the two failures worth naming; anything else gets the generic line.
  const passkeyErrorText = (err: unknown): string => {
    if (err instanceof ApiError) {
      return err.message.includes('HTTPS') ? t('settings.passkeys.needsHttps') : err.message
    }
    if ((err as { name?: string } | null)?.name === 'InvalidStateError') {
      return t('settings.passkeys.alreadyRegistered')
    }
    return t('common.errorOccurred')
  }

  const registerMutation = useMutation({
    mutationFn: async (name: string) => {
      const optionsJSON = await api.auth.passkeyRegisterOptions()
      const response = await startRegistration({ optionsJSON })
      await api.auth.passkeyRegisterVerify({ deviceName: name, response })
    },
    onMutate: () => setError(null),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: qk.passkeys })
      setDeviceName(defaultDeviceName)
      snackbar.success(t('settings.passkeys.added'))
    },
    onError: (err) => {
      if (isUserCancelled(err)) return
      setError(passkeyErrorText(err))
    },
  })

  const removeMutation = useMutation({
    mutationFn: (id: string) => api.auth.removePasskey(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: qk.passkeys })
      setRemoveConfirm(null)
      snackbar.success(t('settings.passkeys.removed'))
    },
    onError: (err) => {
      setRemoveConfirm(null)
      snackbar.error(err instanceof ApiError ? err.message : t('common.errorOccurred'))
    },
  })

  const handleAdd = () => {
    if (registerMutation.isPending) return
    const name = deviceName.trim() || defaultDeviceName
    registerMutation.mutate(name)
  }

  const items = data ?? []

  return (
    <>
      <div className="bg-surface-container border border-outline-variant rounded-lg p-5">
        <h2 className="text-title-md text-on-surface">{t('settings.passkeys.title')}</h2>
        <p className="text-body-sm text-on-surface-variant mt-0.5">{t('settings.passkeys.subtitle')}</p>

        {!supported && (
          <p className="text-body-sm text-on-surface-variant mt-4" role="note">
            {t('settings.passkeys.notSupported')}
          </p>
        )}

        {isLoading ? (
          <div className="mt-4">
            <Spinner />
          </div>
        ) : items.length === 0 ? (
          <p className="text-body-sm text-on-surface-variant mt-4">{t('settings.passkeys.empty')}</p>
        ) : (
          <div className="mt-4 space-y-3">
            {items.map((passkey) => (
              <div
                key={passkey.id}
                className="flex items-center justify-between flex-wrap gap-y-2 p-3 bg-surface-high rounded-md border border-outline-variant"
              >
                <div className="flex-1 min-w-0">
                  <p className="text-body-md text-on-surface font-medium truncate">{passkey.deviceName}</p>
                  <p className="text-body-sm text-on-surface-variant mt-1">
                    {t('settings.passkeys.addedOn', { date: formatters.date(passkey.createdAt) })}
                    {' · '}
                    {t('settings.passkeys.lastUsed')}:{' '}
                    {passkey.lastUsedAt ? formatters.dateTime(passkey.lastUsedAt) : t('settings.passkeys.neverUsed')}
                  </p>
                </div>

                <div className="flex flex-wrap gap-2 min-w-0 md:ml-4 md:flex-shrink-0">
                  <button
                    onClick={() => setRemoveConfirm(passkey)}
                    disabled={removeMutation.isPending}
                    className="px-3 py-2 text-label-sm rounded-full border border-error text-error interactive hover:bg-error/10 active:bg-error/10 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {t('common.remove')}
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {supported && (
          <div className="mt-4 flex flex-col gap-3 sm:flex-row sm:items-end">
            <div className="flex-1 min-w-0">
              <TextField
                label={t('settings.passkeys.deviceName')}
                value={deviceName}
                onChange={setDeviceName}
                fullWidth
              />
            </div>
            <Button
              variant="filled"
              size="sm"
              onClick={handleAdd}
              loading={registerMutation.isPending}
              disabled={removeMutation.isPending}
            >
              {registerMutation.isPending ? t('settings.passkeys.adding') : t('settings.passkeys.addThisDevice')}
            </Button>
          </div>
        )}

        {error && (
          <p className="text-body-sm text-error mt-3" role="alert">
            {error}
          </p>
        )}
      </div>

      <ConfirmDialog
        open={!!removeConfirm}
        title={t('settings.passkeys.removeTitle')}
        message={removeConfirm ? t('settings.passkeys.removeConfirm', { name: removeConfirm.deviceName }) : ''}
        confirmLabel={t('common.remove')}
        destructive
        onConfirm={() => {
          if (removeConfirm) removeMutation.mutate(removeConfirm.id)
        }}
        onCancel={() => setRemoveConfirm(null)}
        loading={removeMutation.isPending}
      />
    </>
  )
}
